<template>
  <div class="auto-load-efficiency-page">
    <div class="page-head">
      <h2 class="page-title">汽车装车作业时效分析</h2>
    </div>

    <el-card shadow="never" class="filter-card">
      <el-form inline :model="query">
        <el-form-item label="作业时间范围">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="yyyy-MM-dd"
            @change="onDateChange"
          />
        </el-form-item>

        <el-form-item label="结算单位">
          <el-select
            v-model="query.custNo"
            clearable
            filterable
            remote
            reserve-keyword
            :loading="custLoading"
            style="min-width: 220px"
            placeholder="输入结算单位名称搜索"
            :remote-method="remoteBalanceCustSearch"
          >
            <el-option v-for="c in custOptions" :key="c.custNo" :label="c.custName" :value="c.custNo" />
          </el-select>
        </el-form-item>

        <el-form-item label="准时状态">
          <el-radio-group v-model="query.delayStatus">
            <el-radio-button label="">全部</el-radio-button>
            <el-radio-button label="normal">准时</el-radio-button>
            <el-radio-button label="delayed">延误</el-radio-button>
          </el-radio-group>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="applyFilter">查询</el-button>
          <el-button type="success" icon="el-icon-download" style="margin-left: 8px;" @click="exportCsv">导出</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-row :gutter="16" class="kpi-row" style="margin-top: 16px;">
      <el-col :xs="24" :sm="12" :md="6">
        <el-card shadow="hover" class="kpi-card">
          <div class="kpi-val">{{ fmtNum(kpi.avgLoadHoursPerCar, 1) }} 小时/车</div>
          <div class="kpi-label">平均装车时长</div>
        </el-card>
      </el-col>

      <el-col :xs="24" :sm="12" :md="6">
        <el-card shadow="hover" class="kpi-card">
          <div class="kpi-val">{{ fmtNum(kpi.completionRatePercent, 1) }}%</div>
          <div class="kpi-label">装车效率</div>
          <div class="kpi-sub">完成率</div>
        </el-card>
      </el-col>

      <el-col :xs="24" :sm="12" :md="6">
        <el-card shadow="hover" class="kpi-card">
          <div class="kpi-val warn">{{ kpi.delayedTrainCount != null ? kpi.delayedTrainCount : 0 }} 车</div>
          <div class="kpi-label">延误车次</div>
          <div class="kpi-sub">延误率 {{ fmtNum(kpi.delayRatePercent, 1) }}%</div>
        </el-card>
      </el-col>

      <el-col :xs="24" :sm="12" :md="6">
        <el-card shadow="hover" class="kpi-card">
          <div class="kpi-val">{{ kpi.arrivedCount != null ? kpi.arrivedCount : 0 }} 车</div>
          <div class="kpi-label">装车完成车次</div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" style="margin-top: 16px;">
      <el-col :xs="24" :lg="12">
        <el-card shadow="hover" class="block-card">
          <div class="card-head">
            <span class="card-title">装车环节耗时漏斗图</span>
          </div>
          <div ref="funnelChart" class="echart-box funnel-h" />
        </el-card>
      </el-col>

      <el-col :xs="24" :lg="12">
        <el-card shadow="hover" class="block-card">
          <div class="card-head">
            <span class="card-title">装车效率趋势</span>
          </div>
          <div ref="trendChart" class="echart-box area-h" />
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" style="margin-top: 16px;">
      <el-col :xs="24" :lg="24">
        <el-card shadow="hover" class="block-card">
          <div class="card-head">
            <span class="card-title">结算单位车次分析</span>
          </div>
          <el-table :data="balanceCustRows" border stripe style="width: 100%">
            <el-table-column prop="balanceCustName" label="结算单位" min-width="180" />
            <el-table-column prop="carCount" label="装车车数" width="110" />
            <el-table-column prop="avgLoadHoursPerCar" label="平均时长(h/车)" width="160" :formatter="fmtHour" />
            <el-table-column prop="delayedCarCount" label="延误车数" width="120" />
            <el-table-column label="状态" width="160">
              <template slot-scope="{ row }">
                <el-tag :type="balanceCustStatusTagType(row)">{{ balanceCustStatusTagText(row) }}</el-tag>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>
    </el-row>

    <el-card shadow="hover" class="block-card" style="margin-top: 16px;">
      <div class="card-head">
        <span class="card-title">装车作业明细</span>
        <div>
          <el-input v-model="tableKeyword" clearable placeholder="车号/车牌" style="width: 180px; margin-right: 8px" @keyup.enter.native="reloadTable" />
          <el-button type="primary" size="small" @click="reloadTable">筛选</el-button>
        </div>
      </div>

      <el-table v-loading="tableLoading" :data="tableRecords" border stripe style="width: 100%">
        <el-table-column prop="operationDate" label="作业日期" width="120" />
        <el-table-column prop="balanceCustName" label="结算单位" min-width="150" show-overflow-tooltip />
        <el-table-column prop="vehicleNo" label="车号" min-width="120" />
        <el-table-column prop="queueFlag" label="车辆状态" width="130">
          <template slot-scope="{ row }">
            <el-tag size="mini">{{ queueFlagText(row.queueFlag) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="ingateTime" label="进门时间" width="170" :formatter="fmtDt" />
        <el-table-column prop="oneTime" label="空车过磅时间" width="170" :formatter="fmtDt" />
        <el-table-column prop="emptyWeighMinutes" label="空车过磅耗时(分)" width="150" align="right" :formatter="fmtInt" />
        <el-table-column prop="loadBeginTime" label="开始装车时间" width="185" :formatter="fmtDt" />
        <el-table-column prop="loadEndTime" label="结束装车时间" width="185" :formatter="fmtDt" />
        <el-table-column prop="loadMinutes" label="装车耗时(分)" width="120" align="right" :formatter="fmtInt" />
        <el-table-column prop="twoTime" label="二磅时间" width="170" :formatter="fmtDt" />
        <el-table-column prop="outgateTime" label="出门时间" width="170" :formatter="fmtDt" />
        <el-table-column prop="exitWaitMinutes" label="出门等待(分)" width="120" align="right" :formatter="fmtInt" />
        <el-table-column prop="loadDurationHours" label="作业时长(h/车)" width="150" align="right" :formatter="fmtHour" />
        <el-table-column prop="delayReason" label="延误原因" min-width="160" show-overflow-tooltip />
        <el-table-column prop="delayMinutes" label="延误时长(分)" width="120" align="right">
          <template slot-scope="{ row }">
            <span :class="delayClass(row.delayMinutes)">{{ fmtDelay(row.delayMinutes) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="140">
          <template slot-scope="{ row }">
            <el-tag :type="delayStatusTagType(row)">{{ delayStatusTagText(row) }}</el-tag>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="pager"
        background
        layout="total, prev, pager, next"
        :total="tableTotal"
        :page-size="pagination.size"
        :current-page.sync="pagination.page"
        @current-change="loadTable"
      />
    </el-card>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import {
  fetchKpi,
  fetchDurationTrend,
  fetchBalanceCustAnalysis,
  fetchDetailPage,
  fetchCustPycodeDistinct,
  fetchLoadStageFunnel
} from './api'

export default {
  name: 'AutoLoadEfficiency',
  data () {
    const today = new Date()
    const fmt = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    return {
      dateRange: [fmt(today), fmt(today)],
      query: {
        custNo: '',
        delayStatus: ''
      },
      kpi: {},
      custOptions: [],
      custLoading: false,
      balanceCustRows: [],
      tableKeyword: '',
      tableRecords: [],
      tableTotal: 0,
      tableLoading: false,
      pagination: { page: 1, size: 10 },
      trendChart: null,
      funnelChart: null
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.trendChart = echarts.init(this.$refs.trendChart)
      this.funnelChart = echarts.init(this.$refs.funnelChart)
      window.addEventListener('resize', this.onResize)
      this.applyFilter()
    })
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.onResize)
    this.trendChart && this.trendChart.dispose()
    this.funnelChart && this.funnelChart.dispose()
  },
  methods: {
    onResize () {
      this.trendChart && this.trendChart.resize()
      this.funnelChart && this.funnelChart.resize()
    },
    num (v, fb) {
      const n = Number(v)
      if (isNaN(n)) return fb
      return n
    },
    fmtNum (v, d) {
      if (v === null || v === undefined || v === '') return '-'
      return Number(v).toFixed(d)
    },
    fmtDt (row, col, val) {
      if (!val) return '-'
      const s = String(val)
      return s.length > 16 ? s.slice(0, 16) : s
    },
    fmtHour (row, col, val) {
      if (val === null || val === undefined || val === '') return '-'
      return Number(val).toFixed(2)
    },
    fmtInt (row, col, val) {
      if (val === null || val === undefined || val === '') return '-'
      const n = Number(val)
      return isNaN(n) ? '-' : Math.round(n)
    },
    fmtDelay (m) {
      if (m === null || m === undefined) return '-'
      const n = Number(m)
      if (n === 0) return '0分钟'
      return `${n}分钟`
    },
    delayClass (m) {
      if (m == null) return ''
      return Number(m) > 0 ? 'delay-pos' : ''
    },
    basePayload () {
      const start = this.dateRange && this.dateRange[0]
      const end = this.dateRange && this.dateRange[1]
      return {
        startTime: start ? `${start} 00:00:00` : undefined,
        endTime: end ? `${end} 23:59:59` : undefined,
        custNo: this.query.custNo || undefined,
        delayStatus: this.query.delayStatus || undefined
      }
    },
    formatDate (d) {
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    },
    async onDateChange () {},
    async remoteBalanceCustSearch (q) {
      const key = q || ''
      if (!key) {
        this.custOptions = []
        return
      }
      this.custLoading = true
      try {
        const { data } = await fetchCustPycodeDistinct(key)
        const raw = Array.isArray(data) ? data : []
        this.custOptions = raw
          .filter((r) => r && r.custNo)
          .map((r) => ({
            custNo: r.custNo,
            custName: r.custName || r.custNo
          }))
      } catch (e) {
        this.custOptions = []
      } finally {
        this.custLoading = false
      }
    },
    async applyFilter () {
      if (!this.dateRange || this.dateRange.length !== 2 || !this.dateRange[0] || !this.dateRange[1]) {
        this.$message.warning('请选择时间区间')
        return
      }
      const start = new Date(this.dateRange[0])
      const end = new Date(this.dateRange[1])
      if (isNaN(start.getTime()) || isNaN(end.getTime()) || start.getTime() > end.getTime()) {
        this.$message.warning('时间区间格式不合法')
        return
      }
      this.pagination.page = 1
      await Promise.all([
        this.loadKpi(),
        this.loadTrend(),
        this.loadBalanceCustAnalysis(),
        this.loadTable()
      ])
    },
    async loadKpi () {
      try {
        const { data } = await fetchKpi(this.basePayload())
        this.kpi = data.data || {}
        this.loadFunnel()
      } catch (e) {
        this.kpi = {}
        this.loadFunnel()
      }
    },
    async loadFunnel () {
      if (!this.funnelChart) return
      try {
        const { data } = await fetchLoadStageFunnel(this.basePayload())
        const d = data.data || {}
        const total = Number(d.totalPlans) || 0
        const emptyWeigh = Number(d.emptyWeighCount) || 0
        const loadingStage = Number(d.loadingStageCount) || 0
        const exitWait = Number(d.exitWaitCount) || 0

        const delaySuffix =
          this.query.delayStatus === 'normal'
            ? '（准时<=2h）'
            : this.query.delayStatus === 'delayed'
              ? '（超时>2h）'
              : ''

        const max = Math.max(total, emptyWeigh, loadingStage, exitWait, 1)
        const seriesData = [
          { value: total, name: '进门岗' },
          { value: emptyWeigh, name: `空车过磅${delaySuffix}` },
          { value: loadingStage, name: `装车环节${delaySuffix}` },
          { value: exitWait, name: `出门等待${delaySuffix}` }
        ]

        this.funnelChart.setOption({
          tooltip: { trigger: 'item' },
          series: [{
            type: 'funnel',
            left: '8%',
            top: 40,
            width: '84%',
            min: 0,
            max,
            sort: 'descending',
            gap: 2,
            label: {
              formatter: '{b}\n{c}({d}%)'
            },
            itemStyle: { borderColor: '#fff', borderWidth: 1 },
            data: seriesData
          }]
        }, true)
      } catch (e) {
        // 接口失败时保持空图，避免报错阻断页面渲染
        this.funnelChart.setOption({
          series: [{ type: 'funnel', data: [] }]
        }, true)
      }
    },
    balanceCustStatusTagType (row) {
      const carCount = Number(row.carCount) || 0
      const delayed = Number(row.delayedCarCount) || 0
      if (!carCount) return 'info'
      const delayedRate = delayed / carCount
      if (delayedRate === 0) return 'success'
      if (delayedRate < 0.2) return 'warning'
      return 'danger'
    },
    balanceCustStatusTagText (row) {
      const carCount = Number(row.carCount) || 0
      const delayed = Number(row.delayedCarCount) || 0
      if (!carCount) return '--'
      const normalRate = ((carCount - delayed) / carCount) * 100
      return `准时 ${normalRate.toFixed(1)}%`
    },
    delayStatusTagType (row) {
      const flag = Number(row.delayFlag) || 0
      const m = Number(row.delayMinutes) || 0
      if (flag === 0) return 'success'
      if (m <= 60) return 'warning'
      return 'danger'
    },
    delayStatusTagText (row) {
      const flag = Number(row.delayFlag) || 0
      const m = Number(row.delayMinutes) || 0
      if (flag === 0) return '正常'
      if (m <= 60) return `轻度延误 ${m}分`
      return `严重延误 ${m}分`
    },
    queueFlagText (flag) {
      const map = {
        10: '已派车',
        11: '预约',
        14: '进停车场',
        16: '签到',
        18: '出停车场',
        19: '车检',
        20: '进门岗',
        30: '过一磅/复磅',
        40: '装卸车/出库',
        50: '过二磅/复磅',
        60: '出门岗',
        99: '已完成'
      }
      if (flag === null || flag === undefined || flag === '') return '-'
      return map[flag] || String(flag)
    },
    async loadTrend () {
      if (!this.trendChart) return
      try {
        const { data } = await fetchDurationTrend(this.basePayload())
        const d = data.data || {}
        const hours = d.hours || []
        const series = d.series || []
        const colors = ['#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399', '#9b59b6', '#00bcd4']
        const toEfficiencyPct = (hoursVal) => {
          const n = Number(hoursVal)
          if (isNaN(n) || n <= 0) return null
          // 以“延误阈值=2小时”为基准：小时越小效率越高
          const pct = (2 / n) * 100
          return Number(Math.max(0, Math.min(100, pct)).toFixed(1))
        }
        const ser = series.map((s, i) => ({
          name: s.name,
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          data: (s.data || []).map((v) => (v != null ? toEfficiencyPct(v) : null)),
          itemStyle: { color: colors[i % colors.length] },
          areaStyle: {
            opacity: 0.25
          }
        }))
        this.trendChart.setOption({
          tooltip: { trigger: 'axis' },
          legend: { show: false },
          grid: { left: '3%', right: '4%', bottom: '8%', top: '12%', containLabel: true },
          xAxis: { type: 'category', data: hours, boundaryGap: false },
          yAxis: { type: 'value', name: '效率(%)', min: 0, max: 100 },
          series: ser
        }, true)
      } catch (e) {
        this.$message.error('趋势图加载失败')
      }
    },
    async loadBalanceCustAnalysis () {
      try {
        const { data } = await fetchBalanceCustAnalysis(this.basePayload())
        const wrap = data.data || {}
        this.balanceCustRows = wrap.list || []
      } catch (e) {
        this.balanceCustRows = []
      }
    },
    async applyTableKeywordPayload () {
      return {
        ...this.basePayload(),
        keyword: this.tableKeyword || undefined,
        page: this.pagination.page,
        size: this.pagination.size
      }
    },
    async loadTable () {
      this.tableLoading = true
      try {
        const payload = await this.applyTableKeywordPayload()
        const { data } = await fetchDetailPage(payload)
        const page = data.data || {}
        this.tableRecords = page.records || []
        this.tableTotal = page.total || 0
      } catch (e) {
        this.tableRecords = []
        this.tableTotal = 0
      } finally {
        this.tableLoading = false
      }
    },
    exportCsv () {
      const rows = this.tableRecords || []
      if (!rows.length) {
        this.$message.warning('暂无可导出的数据')
        return
      }

      const columns = [
        { key: 'operationDate', label: '作业日期' },
        { key: 'balanceCustName', label: '结算单位' },
        { key: 'vehicleNo', label: '车号' },
        { key: 'queueFlag', label: '车辆状态' },
        { key: 'ingateTime', label: '进门时间' },
        { key: 'oneTime', label: '空车过磅时间' },
        { key: 'emptyWeighMinutes', label: '空车过磅耗时(分)' },
        { key: 'loadBeginTime', label: '开始装车时间' },
        { key: 'loadEndTime', label: '结束装车时间' },
        { key: 'loadMinutes', label: '装车耗时(分)' },
        { key: 'twoTime', label: '二磅时间' },
        { key: 'outgateTime', label: '出门时间' },
        { key: 'exitWaitMinutes', label: '出门等待(分)' },
        { key: 'loadDurationHours', label: '作业时长(h/车)' },
        { key: 'delayReason', label: '延误原因' },
        { key: 'delayMinutes', label: '延误时长(分)' },
        { key: 'delayFlag', label: '状态(0正常/1延误)' }
      ]

      const escapeCell = (v) => {
        const s = v === null || v === undefined ? '' : String(v)
        if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`
        return s
      }

      const header = columns.map((c) => c.label).join(',')
      const body = rows
        .map((r) => columns.map((c) => escapeCell(r[c.key])).join(','))
        .join('\n')

      const csv = `${header}\n${body}\n`
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const start = this.dateRange && this.dateRange[0] ? this.dateRange[0] : 'start'
      const end = this.dateRange && this.dateRange[1] ? this.dateRange[1] : 'end'
      a.download = `auto-load-efficiency_${start}_to_${end}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },
    reloadTable () {
      this.pagination.page = 1
      this.loadTable()
    },
    // 班组相关展示已移除
  }
}
</script>

<style scoped>
.kpi-val {
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 6px;
}
.kpi-card {
  background: #fff;
  border-radius: 8px;
  border: 1px solid #ebeef5;
}
.kpi-label {
  color: #606266;
  font-size: 13px;
}
.kpi-sub {
  color: #606266;
  font-size: 13px;
  margin-top: 6px;
}
.kpi-val.warn {
  color: #f56c6c;
}
.echart-box {
  width: 100%;
  height: 320px;
}
.funnel-h {
  height: 320px;
}
.area-h {
  height: 320px;
}
.bar-h {
  height: 320px;
}
.block-card {
  border-radius: 8px;
}
.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.card-title {
  font-weight: 600;
}
.empty-tip {
  text-align: center;
  color: #909399;
  padding: 16px;
}
.delay-pos {
  color: #f56c6c;
  font-weight: 600;
}
.pager {
  margin-top: 12px;
  text-align: right;
}
</style>

